import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { 
  CreditCard, CheckCircle, XCircle, Loader2, Shield, 
  Building2, Clock, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PaymentPage() {
  const { linkId } = useParams();
  const [searchParams] = useSearchParams();
  
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [paymentData, setPaymentData] = useState(null);
  const [error, setError] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null); // 'success', 'failure', 'pending', 'cancelled'
  
  // Check for return parameters
  const ref = searchParams.get('ref');
  const paymentId = searchParams.get('paymentId');
  const payerId = searchParams.get('PayerID');
  
  // Determine page state from URL
  const isSuccessPage = window.location.pathname.includes('/payment/success');
  const isFailurePage = window.location.pathname.includes('/payment/failure');
  const isPendingPage = window.location.pathname.includes('/payment/pending');
  const isCancelPage = window.location.pathname.includes('/payment/cancel');

  useEffect(() => {
    // Handle result pages
    if (isSuccessPage) {
      setPaymentStatus('success');
      setLoading(false);
      return;
    }
    if (isFailurePage) {
      setPaymentStatus('failure');
      setLoading(false);
      return;
    }
    if (isPendingPage) {
      setPaymentStatus('pending');
      setLoading(false);
      return;
    }
    if (isCancelPage) {
      setPaymentStatus('cancelled');
      setLoading(false);
      return;
    }
    
    // Load payment link data
    if (linkId) {
      loadPaymentLink();
    } else {
      setLoading(false);
      setError('No se especificó un link de pago');
    }
  }, [linkId, isSuccessPage, isFailurePage, isPendingPage, isCancelPage]);

  // Handle PayPal return
  useEffect(() => {
    if (paymentId && payerId && !isSuccessPage) {
      executePayPalPayment();
    }
  }, [paymentId, payerId]);

  const loadPaymentLink = async () => {
    try {
      // Determine provider from link ID format
      const isMercadoPago = linkId.startsWith('MP-LINK-');
      const endpoint = isMercadoPago 
        ? `${API_URL}/api/mercadopago/payment-link/${linkId}`
        : `${API_URL}/api/paypal/payment-link/${linkId}`;
      
      const res = await fetch(endpoint);
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || 'Error al cargar el link de pago');
      }
      
      setPaymentData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const executePayPalPayment = async () => {
    setProcessing(true);
    try {
      const res = await fetch(
        `${API_URL}/api/paypal/execute-payment?payment_id=${paymentId}&payer_id=${payerId}`,
        { method: 'POST' }
      );
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || 'Error al procesar el pago');
      }
      
      setPaymentStatus('success');
      toast.success('¡Pago completado exitosamente!');
    } catch (err) {
      setError(err.message);
      toast.error('Error al procesar el pago');
    } finally {
      setProcessing(false);
    }
  };

  const initiatePayment = async () => {
    if (!paymentData) return;
    
    setProcessing(true);
    try {
      const isMercadoPago = paymentData.provider === 'mercadopago';
      
      if (isMercadoPago) {
        // Para Mercado Pago, redirigir directamente al checkout
        if (paymentData.payment_url) {
          window.location.href = paymentData.payment_url;
        } else {
          throw new Error('URL de pago no disponible');
        }
      } else {
        // Para PayPal, crear el pago y obtener URL de aprobación
        const res = await fetch(`${API_URL}/api/paypal/payment-link/${linkId}/pay`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        });
        
        const data = await res.json();
        
        if (!res.ok) {
          throw new Error(data.detail || 'Error al iniciar el pago');
        }
        
        if (data.approval_url) {
          window.location.href = data.approval_url;
        } else {
          throw new Error('No se recibió URL de aprobación');
        }
      }
    } catch (err) {
      setError(err.message);
      toast.error('Error al iniciar el pago');
      setProcessing(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-orange-500 animate-spin mx-auto mb-4" />
          <p className="text-[#AEAEB2]">Cargando información del pago...</p>
        </div>
      </div>
    );
  }

  // Success state
  if (paymentStatus === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
        <div className="bg-white/55 rounded-2xl p-8 max-w-md w-full text-center border border-[rgba(0,0,0,0.06)]">
          <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-500" />
          </div>
          <h1 className="text-2xl font-bold text-[#1D1D1F] mb-2">¡Pago Exitoso!</h1>
          <p className="text-[#AEAEB2] mb-6">
            Tu pago ha sido procesado correctamente. Recibirás un correo de confirmación.
          </p>
          {ref && (
            <div className="bg-gray-700/50 rounded-xl p-4 mb-6">
              <p className="text-sm text-[#AEAEB2]">Referencia</p>
              <p className="text-lg font-mono text-green-400">{ref}</p>
            </div>
          )}
          <p className="text-sm text-[#AEAEB2]">
            Gracias por tu confianza en Paliacate Studio
          </p>
        </div>
      </div>
    );
  }

  // Pending state
  if (paymentStatus === 'pending') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
        <div className="bg-white/55 rounded-2xl p-8 max-w-md w-full text-center border border-[rgba(0,0,0,0.06)]">
          <div className="w-20 h-20 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Clock className="w-10 h-10 text-yellow-500" />
          </div>
          <h1 className="text-2xl font-bold text-[#1D1D1F] mb-2">Pago Pendiente</h1>
          <p className="text-[#AEAEB2] mb-6">
            Tu pago está siendo procesado. Esto puede tomar unos minutos.
            Te notificaremos cuando se confirme.
          </p>
          {ref && (
            <div className="bg-gray-700/50 rounded-xl p-4 mb-6">
              <p className="text-sm text-[#AEAEB2]">Referencia</p>
              <p className="text-lg font-mono text-yellow-400">{ref}</p>
            </div>
          )}
          <p className="text-sm text-[#AEAEB2]">
            Si pagaste en OXXO o transferencia, el proceso puede tomar hasta 24 horas.
          </p>
        </div>
      </div>
    );
  }

  // Failure/Cancelled state
  if (paymentStatus === 'failure' || paymentStatus === 'cancelled') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
        <div className="bg-white/55 rounded-2xl p-8 max-w-md w-full text-center border border-[rgba(0,0,0,0.06)]">
          <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <XCircle className="w-10 h-10 text-red-500" />
          </div>
          <h1 className="text-2xl font-bold text-[#1D1D1F] mb-2">
            {paymentStatus === 'cancelled' ? 'Pago Cancelado' : 'Error en el Pago'}
          </h1>
          <p className="text-[#AEAEB2] mb-6">
            {paymentStatus === 'cancelled' 
              ? 'Has cancelado el proceso de pago. Puedes intentarlo nuevamente cuando lo desees.'
              : 'Hubo un problema al procesar tu pago. Por favor intenta de nuevo.'}
          </p>
          <button
            onClick={() => window.history.back()}
            className="px-6 py-3 bg-orange-500 text-white rounded-xl font-medium hover:bg-orange-600 transition-colors"
          >
            Volver a intentar
          </button>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
        <div className="bg-white/55 rounded-2xl p-8 max-w-md w-full text-center border border-[rgba(0,0,0,0.06)]">
          <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-10 h-10 text-red-500" />
          </div>
          <h1 className="text-2xl font-bold text-[#1D1D1F] mb-2">Error</h1>
          <p className="text-[#AEAEB2] mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-orange-500 text-white rounded-xl font-medium hover:bg-orange-600 transition-colors"
          >
            Intentar de nuevo
          </button>
        </div>
      </div>
    );
  }

  // Payment form
  const isMercadoPago = paymentData?.provider === 'mercadopago';
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
      <div className="bg-white/55 rounded-2xl overflow-hidden max-w-md w-full border border-[rgba(0,0,0,0.06)]">
        {/* Header */}
        <div className={`p-6 text-[#1D1D1F] ${isMercadoPago 
          ? 'bg-gradient-to-r from-[#009ee3] to-[#00b1ea]' 
          : 'bg-gradient-to-r from-[#0070ba] to-[#003087]'}`}
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <CreditCard className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-xl font-bold">Paliacate Studio</h1>
              <p className="text-sm text-[#1D1D1F]/80">Pago Seguro</p>
            </div>
          </div>
          {paymentData?.business_name && (
            <div className="flex items-center gap-2 text-sm text-[#1D1D1F]/80">
              <Building2 className="w-4 h-4" />
              <span>{paymentData.business_name}</span>
            </div>
          )}
        </div>
        
        {/* Amount */}
        <div className="p-6 border-b border-[rgba(0,0,0,0.06)]">
          <p className="text-sm text-[#AEAEB2] mb-1">Monto a pagar</p>
          <p className="text-4xl font-bold text-[#1D1D1F]">
            ${paymentData?.amount?.toLocaleString()} <span className="text-xl text-[#AEAEB2]">{paymentData?.currency}</span>
          </p>
          {paymentData?.description && (
            <p className="text-[#AEAEB2] mt-3 text-sm">{paymentData.description}</p>
          )}
          
          {/* Breakdown if available */}
          {paymentData?.breakdown && (
            <div className="mt-4 pt-4 border-t border-[rgba(0,0,0,0.06)] space-y-2 text-sm">
              <div className="flex justify-between text-[#AEAEB2]">
                <span>Monto base:</span>
                <span>${paymentData.breakdown.base_amount?.toLocaleString()}</span>
              </div>
              {paymentData.breakdown.includes_iva && (
                <div className="flex justify-between text-cyan-400">
                  <span>IVA (16%):</span>
                  <span>${paymentData.breakdown.iva_amount?.toLocaleString()}</span>
                </div>
              )}
              {paymentData.breakdown.includes_commission && (
                <div className="flex justify-between text-orange-400">
                  <span>Comisión (5%):</span>
                  <span>${paymentData.breakdown.commission_amount?.toLocaleString()}</span>
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Payment Button */}
        <div className="p-6">
          <button
            onClick={initiatePayment}
            disabled={processing}
            className={`w-full py-4 text-[#1D1D1F] rounded-xl font-bold text-lg flex items-center justify-center gap-3 transition-colors disabled:opacity-50 ${
              isMercadoPago 
                ? 'bg-[#009ee3] hover:bg-[#0087c5]' 
                : 'bg-[#0070ba] hover:bg-[#005ea6]'
            }`}
          >
            {processing ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Procesando...
              </>
            ) : (
              <>
                {isMercadoPago ? (
                  <>
                    <span className="text-2xl">💳</span>
                    Pagar con Mercado Pago
                  </>
                ) : (
                  <>
                    <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor">
                      <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c1.087 3.066-1.05 5.954-5.23 5.954h-2.19c-1.574 0-2.905 1.146-3.15 2.7l-1.12 7.106a.641.641 0 0 0 .633.74h3.65a.95.95 0 0 0 .937-.804l.918-5.823a.95.95 0 0 1 .936-.804h1.895c3.823 0 6.812-1.555 7.687-6.044.35-1.798.073-3.263-.82-4.296a4.42 4.42 0 0 0-1.054-.847c-.482-.27-1.023-.483-1.485-.341z"/>
                    </svg>
                    Pagar con PayPal
                  </>
                )}
              </>
            )}
          </button>
          
          {/* Payment methods info for Mercado Pago */}
          {isMercadoPago && (
            <p className="text-center text-xs text-[#AEAEB2] mt-3">
              Tarjeta • Transferencia • OXXO • SPEI
            </p>
          )}
          
          {/* Security Note */}
          <div className="flex items-center justify-center gap-2 mt-4 text-xs text-[#AEAEB2]">
            <Shield className="w-4 h-4" />
            <span>Pago seguro procesado por {isMercadoPago ? 'Mercado Pago' : 'PayPal'}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
